import { useState, useEffect, useMemo } from 'react';
import { getAllContent, saveContent, publishContent, deleteContent } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import ImageUploader from '../components/ImageUploader';
import RichTextField from '../components/RichTextField';
import NewsPreview from '../components/NewsPreview';
import { richTextAllowed } from '../utils/richText';
import Upgrade from './Upgrade';
import './News.css';
import { overlayClose } from '../utils/overlayClose';

interface NewsArticle {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  date: string;
  image: string;
  images: string[];
  published: boolean;
}

/** Extra photos on an article, as well as the main image. The server holds
 *  the same limit (lambda/lib/news-photos.ts). */
const MAX_PHOTOS = 6;

function News() {
  const { plan, features, clubId } = useAuth();
  // The formatting bar comes with the plan, like the rest of this page: News
  // itself is Club and Pro only, so every club that can reach this screen has
  // it. The flag survives as an operator override for a one-off.
  //
  // Derived rather than stored, for the reason match reports taught us: a
  // stored flag drifts from the plan and ends up on the wrong clubs.
  const richTextOn = richTextAllowed(plan, features);

  if (plan === 'free') {
    return <Upgrade />;
  }

  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState<NewsArticle | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [date, setDate] = useState('');
  const [image, setImage] = useState('');
  const [images, setImages] = useState<string[]>([]);
  // Pictures the article already had when it was opened: only a photo added
  // since needs the permission tick.
  const [hadPhotos, setHadPhotos] = useState<string[]>([]);
  const [photoConsent, setPhotoConsent] = useState(false);
  const [uploads, setUploads] = useState(0);
  const trackUpload = (u: boolean) => setUploads(c => Math.max(0, c + (u ? 1 : -1)));
  const newPhotos = images.some(url => !hadPhotos.includes(url));
  // What the preview beside the editor shows: the article as it stands, unsaved.
  const previewArticle = useMemo(
    () => ({ title, date, content, image, images }),
    [title, date, content, image, images],
  );

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      setIsLoading(true);
      const allDraft = await getAllContent('draft');
      const allPublished = await getAllContent('published');

      const newsItems = allDraft
        .filter(item => item.section.startsWith('news-'))
        .map(item => {
          const data = item.content as Record<string, any>;
          const isPublished = allPublished.some(p => p.section === item.section);
          return {
            id: item.section,
            title: data.title || '',
            excerpt: data.excerpt || '',
            content: data.content || '',
            date: data.date || '',
            image: data.image || '',
            images: Array.isArray(data.images) ? data.images : [],
            published: isPublished,
          };
        })
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setArticles(newsItems);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load news');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setExcerpt('');
    setContent('');
    setDate(new Date().toISOString().slice(0, 10));
    setImage('');
    setImages([]);
    setHadPhotos([]);
    setPhotoConsent(false);
    setSelectedArticle(null);
    setIsEditing(false);
  };

  const handleNew = () => {
    resetForm();
    setIsEditing(true);
  };

  const handleEdit = (article: NewsArticle) => {
    setSelectedArticle(article);
    setTitle(article.title);
    setExcerpt(article.excerpt);
    setContent(article.content);
    setDate(article.date);
    setImage(article.image);
    setImages(article.images);
    setHadPhotos([article.image, ...article.images].filter(Boolean));
    setPhotoConsent(false);
    setIsEditing(true);
    setError('');
    setSuccessMessage('');
  };

  const handleSave = async (andPublish: boolean) => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (uploads > 0) {
      setError('A photo is still uploading. Give it a moment, then save.');
      return;
    }
    if (newPhotos && !photoConsent) {
      setError('Tick the box to confirm you have the parents\' permission for any children in the photos.');
      return;
    }
    setError('');
    setSuccessMessage('');
    setIsSaving(true);

    try {
      const articleId = selectedArticle?.id || `news-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
      const data = {
        type: 'news',
        title: title.trim(),
        excerpt: excerpt.trim(),
        content: content.trim(),
        date: date || new Date().toISOString().split('T')[0],
        image,
        images,
        ...(newPhotos ? { imagesConsent: photoConsent } : {}),
      };

      await saveContent(articleId, data);

      if (andPublish) {
        await publishContent(articleId);
        setSuccessMessage('Article published - now visible on the website');
      } else {
        setSuccessMessage('Draft saved');
      }

      await loadArticles();
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedArticle) return;
    setIsSaving(true);
    try {
      await deleteContent(selectedArticle.id);
      setSuccessMessage('Article deleted');
      setShowDeleteConfirm(false);
      await loadArticles();
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="loading">Loading news...</div>;
  }

  return (
    <div className={`news-admin${isEditing ? ' news-admin--editing' : ''}`}>
      <div className="page-header">
        <div>
          <h1 className="page-title">News & Updates</h1>
          <p className="page-subtitle">Manage news articles shown on the website</p>
        </div>
        <button onClick={handleNew} className="btn btn-primary">
          + New Article
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="modal-overlay" {...overlayClose(() => setShowDeleteConfirm(false))}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Delete Article</h3>
            <p>Are you sure you want to delete "{selectedArticle?.title}"? This cannot be undone.</p>
            <div className="modal-actions">
              <button onClick={() => setShowDeleteConfirm(false)} className="btn btn-outline">Cancel</button>
              <button onClick={handleDelete} className="btn btn-danger" disabled={isSaving}>
                {isSaving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isEditing ? (
        <div className="news-editor">
          <div className="editor-header">
            <h2>{selectedArticle ? 'Edit Article' : 'New Article'}</h2>
            <div className="editor-actions">
              {selectedArticle && (
                <button onClick={() => setShowDeleteConfirm(true)} className="btn btn-outline btn-danger-outline">
                  Delete
                </button>
              )}
              <button onClick={resetForm} className="btn btn-outline">Cancel</button>
              <button onClick={() => handleSave(false)} className="btn btn-secondary" disabled={isSaving || uploads > 0}>
                {isSaving ? 'Saving...' : uploads > 0 ? 'Uploading...' : 'Save Draft'}
              </button>
              <button onClick={() => handleSave(true)} className="btn btn-primary" disabled={isSaving || uploads > 0}>
                {isSaving ? 'Publishing...' : uploads > 0 ? 'Uploading...' : 'Publish'}
              </button>
            </div>
          </div>

          <div className="news-editor-body">
          <div className="news-form">
            <div className="form-group">
              <label className="form-label">Title</label>
              <input
                type="text"
                className="form-input"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Article title"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Date</label>
              <input
                type="date"
                className="form-input"
                value={date}
                onChange={e => setDate(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Excerpt</label>
              <textarea
                className="form-textarea"
                value={excerpt}
                onChange={e => setExcerpt(e.target.value)}
                rows={2}
                placeholder="Short summary shown on news cards"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Full Article</label>
              {richTextOn ? (
                /* Formatting bar. Everything it produces is sanitised on the
                   server at save and again on the website at render, so this is
                   a convenience and never a control. */
                <RichTextField
                  value={content}
                  onChange={setContent}
                  placeholder="Write the article. Use the bar above for headings, bold and lists."
                />
              ) : (
                <textarea
                  className="form-textarea"
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  rows={12}
                  placeholder="Full article text. Use blank lines to separate paragraphs."
                />
              )}
            </div>

            <div className="form-group">
              <ImageUploader
                currentImage={image || undefined}
                onUpload={url => setImage(url)}
                onUploadingChange={trackUpload}
                label="Article Image (optional)"
              />
            </div>

            <div className="form-group">
              <label className="form-label">More photos</label>
              <p className="form-hint">Up to {MAX_PHOTOS}, shown in a grid under the article. The main image above is the one on news cards.</p>
              {images.length > 0 && (
                <div className="news-photos">
                  {images.map((url, i) => (
                    <div key={url} className="news-photo">
                      <img src={url} alt={`Photo ${i + 1}`} />
                      <div className="news-photo-actions">
                        <button type="button" className="btn btn-secondary btn-small" onClick={() => {
                          // Swap with the main image, which takes this photo's place.
                          const rest = images.filter((_, j) => j !== i);
                          if (image) rest.splice(i, 0, image);
                          setImage(url);
                          setImages(rest);
                        }}>Make main image</button>
                        <button type="button" className="btn btn-secondary btn-small"
                                onClick={() => setImages(images.filter((_, j) => j !== i))}>Remove</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {images.length < MAX_PHOTOS && (
                <ImageUploader
                  multiple
                  noPreview
                  label=""
                  maxFiles={MAX_PHOTOS - images.length}
                  onUploadingChange={trackUpload}
                  onUpload={url => setImages(prev => (prev.length < MAX_PHOTOS && !prev.includes(url) ? [...prev, url] : prev))}
                />
              )}
              {newPhotos && (
                <label className="news-consent">
                  <input type="checkbox" checked={photoConsent} onChange={e => setPhotoConsent(e.target.checked)} />
                  <span>
                    I have the parents' permission for any children in these photos to appear
                    on the club's public website.
                  </span>
                </label>
              )}
            </div>
          </div>
          {clubId && (
            <aside className="news-editor-preview">
              <NewsPreview clubId={clubId} article={previewArticle} />
            </aside>
          )}
          </div>
        </div>
      ) : (
        <div className="news-list">
          {articles.length === 0 ? (
            <div className="empty-state">
              <p>No news articles yet. Click "New Article" to create your first one.</p>
            </div>
          ) : (
            articles.map(article => (
              <div key={article.id} className="news-list-item" onClick={() => handleEdit(article)}>
                {article.image && (
                  <img src={article.image} alt="" className="news-list-image" />
                )}
                <div className="news-list-content">
                  <div className="news-list-meta">
                    <span className="news-list-date">
                      {article.date ? new Date(article.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'No date'}
                    </span>
                    <span className={`status-badge ${article.published ? 'published' : 'draft'}`}>
                      {article.published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <h3 className="news-list-title">{article.title || 'Untitled'}</h3>
                  <p className="news-list-excerpt">{article.excerpt || 'No excerpt'}</p>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default News;
